import React, { useState, useEffect } from 'react';
import './ProfileImageSelector.css';

// Static array of all profile images from public/profile_pics/
const PROFILE_IMAGES = [
  '/profile_pics/astronaut_scout.png',
  '/profile_pics/cap_gown_scout.png',
  '/profile_pics/detective_scout.png',
  '/profile_pics/DND_scout.png',
  '/profile_pics/drowsy_scout.png',
  '/profile_pics/engineer_scout.png',
  '/profile_pics/garage_band_scout.png',
  '/profile_pics/hacker_scout.png',
  '/profile_pics/jedi_scout.png',
  '/profile_pics/oil_painting_scout.png',
  '/profile_pics/pixar_paintng_scout.png',
  '/profile_pics/safari_scout.png',
  '/profile_pics/surfer_scout.png',
  '/profile_pics/tailgate_scout.png',
  '/profile_pics/venetian_scout.png'
];

// 20 frame styles - CSS class configurations (no new image files needed)
const FRAME_STYLES = [
  { 
    id: 'classic-black', 
    name: 'Classic Black', 
    className: 'frame-classic-black'
  },
  { 
    id: 'silver-minimal', 
    name: 'Silver Minimal', 
    className: 'frame-silver-minimal'
  },
  { 
    id: 'gold-premium', 
    name: 'Gold Premium', 
    className: 'frame-gold-premium'
  },
  { 
    id: 'docuhounds-blue', 
    name: 'DocuHounds Blue', 
    className: 'frame-docuhounds-blue'
  },
  { 
    id: 'neon-blue', 
    name: 'Neon Blue', 
    className: 'frame-neon-blue'
  },
  { 
    id: 'neon-purple', 
    name: 'Neon Purple', 
    className: 'frame-neon-purple'
  },
  { 
    id: 'pixel-white', 
    name: 'Pixel White', 
    className: 'frame-pixel-white'
  },
  { 
    id: 'rounded-pill', 
    name: 'Rounded Pill', 
    className: 'frame-rounded-pill'
  },
  { 
    id: 'shadow-soft', 
    name: 'Soft Shadow', 
    className: 'frame-shadow-soft'
  },
  { 
    id: 'double-line', 
    name: 'Double Line', 
    className: 'frame-double-line'
  },
  { 
    id: 'grad-blue-purple', 
    name: 'Blue–Purple Gradient', 
    className: 'frame-grad-blue-purple'
  },
  { 
    id: 'grad-orange-pink', 
    name: 'Sunset Gradient', 
    className: 'frame-grad-orange-pink'
  },
  { 
    id: 'glass-morph', 
    name: 'Glass', 
    className: 'frame-glass-morph'
  },
  { 
    id: 'cardboard', 
    name: 'Cardboard', 
    className: 'frame-cardboard'
  },
  { 
    id: 'cooler-lid', 
    name: 'Cooler Lid', 
    className: 'frame-cooler-lid'
  },
  { 
    id: 'wireframe', 
    name: 'Wireframe', 
    className: 'frame-wireframe'
  },
  { 
    id: 'stadium-lights', 
    name: 'Stadium Lights', 
    className: 'frame-stadium-lights'
  },
  { 
    id: 'study-hall', 
    name: 'Study Hall', 
    className: 'frame-study-hall'
  },
  { 
    id: 'dark-mode', 
    name: 'Dark Mode', 
    className: 'frame-dark-mode'
  },
  { 
    id: 'minimal-hairline', 
    name: 'Hairline', 
    className: 'frame-minimal-hairline'
  }
];

export default function ProfileImageSelector({ 
  isOpen, 
  onClose, 
  selectedImage, 
  selectedFrameId, 
  onSave 
}) {
  const [localSelectedImage, setLocalSelectedImage] = useState(selectedImage || PROFILE_IMAGES[0]);
  const [localSelectedFrameId, setLocalSelectedFrameId] = useState(selectedFrameId || 'minimal-hairline');

  // Update local state when props change
  useEffect(() => {
    if (selectedImage) setLocalSelectedImage(selectedImage);
    if (selectedFrameId) setLocalSelectedFrameId(selectedFrameId);
  }, [selectedImage, selectedFrameId]);

  // Close on ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(localSelectedImage, localSelectedFrameId);
  };

  const selectedFrame = FRAME_STYLES.find(f => f.id === localSelectedFrameId) || FRAME_STYLES[0];

  return (
    <div className="profile-selector-overlay" onClick={onClose}>
      <div className="profile-selector-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="profile-selector-header">
          <h2 className="profile-selector-title">Choose Your Profile Picture</h2>
          <button 
            className="profile-selector-close" 
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body - 2 column layout */}
        <div className="profile-selector-body">
          {/* Left column - Image selection grid */}
          <div className="profile-selector-left">
            <h3 className="profile-selector-section-title">Choose your Scout picture</h3>
            <div className="profile-images-grid">
              {PROFILE_IMAGES.map((imagePath) => (
                <button
                  key={imagePath}
                  className={`profile-image-option ${localSelectedImage === imagePath ? 'selected' : ''}`}
                  onClick={() => setLocalSelectedImage(imagePath)}
                  aria-label={`Select ${imagePath.split('/').pop().split('.')[0].replace(/_/g, ' ')}`}
                >
                  <img 
                    src={imagePath} 
                    alt={imagePath.split('/').pop().split('.')[0].replace(/_/g, ' ')}
                    className="profile-image-option-img"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right column - Preview + Frame selection */}
          <div className="profile-selector-right">
            {/* Live Preview */}
            <div className="profile-preview-section">
              <h3 className="profile-selector-section-title">Preview</h3>
              <div className="profile-preview-container">
                <div className={`profile-preview-frame ${selectedFrame.className}`}>
                  <img 
                    src={localSelectedImage} 
                    alt="Preview" 
                    className="profile-preview-img"
                  />
                </div>
                <p className="profile-preview-label">{selectedFrame.name}</p>
              </div>
            </div>

            {/* Frame selection */}
            <div className="frame-selection-section">
              <h3 className="profile-selector-section-title">Choose a frame</h3>
              <div className="frame-options-grid">
                {FRAME_STYLES.map((frame) => (
                  <button
                    key={frame.id}
                    className={`frame-option ${localSelectedFrameId === frame.id ? 'selected' : ''}`}
                    onClick={() => setLocalSelectedFrameId(frame.id)}
                    title={frame.name}
                    aria-label={`Select ${frame.name} frame`}
                  >
                    <div className={`frame-option-preview ${frame.className}`}>
                      <img 
                        src={localSelectedImage} 
                        alt={frame.name}
                        className="frame-option-img"
                      />
                    </div>
                    <span className="frame-option-name">{frame.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="profile-selector-footer">
          <button 
            className="profile-selector-btn profile-selector-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="profile-selector-btn profile-selector-btn-save"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
